import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';

/**
 * Generic N-step guided walkthrough. Used across the app — any feature
 * area can build a `steps` array of `{icon, title, body}` and pass it
 * in. Provides Skip / Back / Next / Finish navigation, indicator dots,
 * and an optional `onFinish` callback for "Try it now" wiring.
 *
 * Props:
 *   open, onOpenChange — controlled visibility
 *   steps — array of { icon: LucideIcon, title: string, body: ReactNode }
 *   tourName — used in DialogDescription, e.g. "Banking Auto-Rules walkthrough"
 *   testidPrefix — defaults to "tour"; data-testids will be:
 *                  `{prefix}-dialog`, `{prefix}-step-{i}`, `{prefix}-skip|prev|next|finish`
 *   onFinish — optional callback fired when user clicks the last-step CTA.
 *              When provided, finish button reads "Try it now ✨" and
 *              triggers this callback (closing the dialog first).
 *   finishLabel — optional override for the finish-button label
 */
export function PageTour({
  open,
  onOpenChange,
  steps,
  tourName = 'Walkthrough',
  testidPrefix = 'tour',
  onFinish,
  finishLabel,
}) {
  const [step, setStep] = useState(0);
  const last = steps.length - 1;
  const s = steps[step] || steps[0];
  const Icon = s?.icon;

  const close = () => { setStep(0); onOpenChange(false); };
  const next = () => setStep((i) => Math.min(i + 1, last));
  const prev = () => setStep((i) => Math.max(i - 1, 0));
  const finish = () => {
    close();
    if (onFinish) onFinish();
  };

  if (!s) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-lg" data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {Icon ? <Icon size={18} className="text-violet-600" /> : null} {s.title}
          </DialogTitle>
          <DialogDescription>
            Step {step + 1} of {steps.length} — {tourName}
          </DialogDescription>
        </DialogHeader>
        <div className="text-sm leading-relaxed text-muted-foreground" data-testid={`${testidPrefix}-step-${step}`}>
          {s.body}
        </div>
        <div className="flex items-center justify-center gap-1.5 mt-2">
          {steps.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setStep(i)}
              aria-label={`Jump to step ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-violet-600' : 'w-1.5 bg-stone-300 hover:bg-stone-400'}`}
            />
          ))}
        </div>
        <DialogFooter className="flex-row !justify-between">
          <Button variant="ghost" onClick={close} className="text-muted-foreground" data-testid={`${testidPrefix}-skip`}>
            Skip
          </Button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button variant="outline" onClick={prev} data-testid={`${testidPrefix}-prev`}>
                <ChevronLeft size={14} className="mr-1" /> Back
              </Button>
            )}
            {step < last ? (
              <Button onClick={next} className="bg-violet-600 hover:bg-violet-700" data-testid={`${testidPrefix}-next`}>
                Next <ChevronRight size={14} className="ml-1" />
              </Button>
            ) : onFinish ? (
              <Button onClick={finish} className="bg-violet-600 hover:bg-violet-700" data-testid={`${testidPrefix}-finish`}>
                {finishLabel || 'Try it now'} <Sparkles size={13} className="ml-1.5" />
              </Button>
            ) : (
              <Button onClick={close} className="bg-violet-600 hover:bg-violet-700" data-testid={`${testidPrefix}-finish`}>
                Got it
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
